/**
 * `diagram/sidecar.ts` — the `%% @aletheia:v1 {json}` layer (interactive-diagrams-v0.1.md §3).
 *
 * The sidecar is how Aletheia carries geometry (node positions, sizes, edge waypoints,
 * viewport) *inside* a mermaid fence without breaking vanilla mermaid. It is a **plain `%%`
 * comment** — never a `%%{…}%%` directive — because mermaid *interprets* directives but
 * guarantees to ignore comments. Any other renderer (GitHub, Obsidian, Claude) sees a
 * comment and drops it; copy-paste carries the layout because the layout is just text.
 *
 * This module owns four operations (plan §3):
 *   1. **extract**  — pull `@aletheia` lines out of source, parse+validate their JSON.
 *   2. **merge**    — combine multiple sidecar lines / an incoming patch (last-wins, deep).
 *   3. **serialize**— render a `SidecarData` back to a single canonical comment line.
 *   4. **reconcile**— drop overrides for ids/edges no longer in the graph (prune orphans).
 *
 * Robustness posture: a malformed or unknown-version sidecar is treated as **absent**, never
 * as an error and never destructively — the diagram still renders (vanilla), and the bad line
 * is preserved verbatim as a comment statement by the parser. We only ever *rewrite* the
 * sidecar line when the caller explicitly mutates geometry.
 */

import {
  type Point,
  type SidecarData,
  type StickyNote,
  type Swimlane,
  type DiagramModel,
  SIDECAR_VERSION,
  emptySidecar,
} from './model';

/**
 * Matches an Aletheia sidecar comment line. Mermaid line comments open with `%%`; we then
 * require the `@aletheia:<version>` tag and a JSON object. Leading whitespace is allowed
 * (a sidecar line may be indented inside a `subgraph`). The `%%` must be the first
 * non-space content — this is a comment, not a trailing annotation.
 *
 * Capture groups: 1 = version (digits), 2 = the JSON object text (greedy to end of line).
 */
export const SIDECAR_RE = /^\s*%%\s*@aletheia:(\d+)\s+(\{.*\})\s*$/;

/** True if a line is an Aletheia sidecar comment (well-formed tag; JSON validity checked later). */
export function isSidecarLine(line: string): boolean {
  return SIDECAR_RE.test(line);
}

/**
 * Parse a single sidecar line into a validated `SidecarData`, or `null` if the line is not a
 * sidecar, the JSON is invalid, or the version is unknown. Never throws.
 *
 * Unknown version → `null` (treat as absent, per §3 "unknown version → render vanilla, never
 * destroy the line"). The parser keeps the raw line regardless, so returning null here loses
 * no bytes — it only means "we don't understand this, so don't apply it".
 */
export function parseSidecarLine(line: string): SidecarData | null {
  const m = SIDECAR_RE.exec(line);
  if (!m) return null;
  const version = Number(m[1]);
  if (version !== SIDECAR_VERSION) return null;
  let json: unknown;
  try {
    json = JSON.parse(m[2]);
  } catch {
    return null;
  }
  return coerceSidecar(json, version);
}

/**
 * Coerce an arbitrary parsed-JSON value into a well-formed `SidecarData`, discarding any
 * field that doesn't match the expected shape. Defensive by construction: a hand-edited or
 * agent-written sidecar with a stray field or wrong type yields a valid (possibly emptier)
 * payload rather than a crash. Returns null only if the top level isn't an object.
 */
function coerceSidecar(json: unknown, version: number): SidecarData | null {
  if (typeof json !== 'object' || json === null || Array.isArray(json)) return null;
  const obj = json as Record<string, unknown>;
  const out: SidecarData = { version, pos: {}, size: {}, edge: {} };

  if (typeof obj.id === 'string') out.id = obj.id;
  out.pos = coercePointMap(obj.pos);
  out.size = coercePointMap(obj.size);
  out.edge = coerceEdgeMap(obj.edge);

  const notes = coerceNotes(obj.notes);
  if (notes.length > 0) out.notes = notes;
  const lanes = coerceLanes(obj.lanes);
  if (lanes.length > 0) out.lanes = lanes;

  const canvas = obj.canvas;
  if (typeof canvas === 'object' && canvas !== null) {
    const c = canvas as Record<string, unknown>;
    const center = coercePoint(c.center);
    if (typeof c.zoom === 'number' && Number.isFinite(c.zoom) && center) {
      out.canvas = { zoom: c.zoom, center };
    }
  }
  return out;
}

/** Coerce a value into `[number, number]`, or null if it isn't two finite numbers. */
function coercePoint(v: unknown): Point | null {
  if (
    Array.isArray(v) &&
    v.length === 2 &&
    typeof v[0] === 'number' &&
    typeof v[1] === 'number' &&
    Number.isFinite(v[0]) &&
    Number.isFinite(v[1])
  ) {
    return [v[0], v[1]];
  }
  return null;
}

/** Coerce a `{ id: [x,y] }` map, dropping malformed entries. */
function coercePointMap(v: unknown): Record<string, Point> {
  const out: Record<string, Point> = {};
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return out;
  for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
    const p = coercePoint(val);
    if (p) out[k] = p;
  }
  return out;
}

/** Optional string field, or undefined. */
function optStr(v: unknown): string | undefined {
  return typeof v === 'string' ? v : undefined;
}

/**
 * Coerce a `notes` array of sticky-note objects, dropping any entry missing a valid id / position.
 * Defensive like the rest of the sidecar: a hand-edited or agent-written note with junk fields
 * yields a valid (possibly emptier) note rather than a crash.
 */
function coerceNotes(v: unknown): StickyNote[] {
  if (!Array.isArray(v)) return [];
  const out: StickyNote[] = [];
  for (const raw of v) {
    if (typeof raw !== 'object' || raw === null) continue;
    const o = raw as Record<string, unknown>;
    const pos = coercePoint(o.pos);
    if (typeof o.id !== 'string' || !pos) continue;
    const note: StickyNote = { id: o.id, text: typeof o.text === 'string' ? o.text : '', pos };
    const size = coercePoint(o.size);
    if (size) note.size = size;
    const color = optStr(o.color);
    if (color) note.color = color;
    out.push(note);
  }
  return out;
}

/** Coerce a `lanes` array of swimlane objects, dropping entries missing a valid id / pos / size. */
function coerceLanes(v: unknown): Swimlane[] {
  if (!Array.isArray(v)) return [];
  const out: Swimlane[] = [];
  for (const raw of v) {
    if (typeof raw !== 'object' || raw === null) continue;
    const o = raw as Record<string, unknown>;
    const pos = coercePoint(o.pos);
    const size = coercePoint(o.size);
    if (typeof o.id !== 'string' || !pos || !size) continue;
    const lane: Swimlane = { id: o.id, label: typeof o.label === 'string' ? o.label : '', pos, size };
    const color = optStr(o.color);
    if (color) lane.color = color;
    if (o.orientation === 'vertical' || o.orientation === 'horizontal') lane.orientation = o.orientation;
    out.push(lane);
  }
  return out;
}

/** Coerce a `{ "A->B:0": { wp: [[x,y],…] } }` map, dropping malformed entries. */
function coerceEdgeMap(v: unknown): Record<string, { wp: Point[] }> {
  const out: Record<string, { wp: Point[] }> = {};
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return out;
  for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
    if (typeof val !== 'object' || val === null) continue;
    const wpRaw = (val as Record<string, unknown>).wp;
    if (!Array.isArray(wpRaw)) continue;
    const wp: Point[] = [];
    for (const p of wpRaw) {
      const pt = coercePoint(p);
      if (pt) wp.push(pt);
    }
    out[k] = { wp };
  }
  return out;
}

/**
 * Deep-merge sidecar payloads, last-wins at the leaf level (plan §3 "last-wins-merged").
 * Used to (a) fold multiple `@aletheia` lines from one fence into one payload, and (b) apply
 * an incoming patch (e.g. a canvas drag) onto the existing sidecar. `id` and `canvas` take
 * the later value when present; `pos`/`size`/`edge` merge key-by-key.
 *
 * Neither input is mutated.
 */
export function mergeSidecar(base: SidecarData | null, patch: SidecarData | null): SidecarData | null {
  if (!base) return patch ? cloneSidecar(patch) : null;
  if (!patch) return cloneSidecar(base);
  const out = cloneSidecar(base);
  out.version = patch.version || out.version;
  if (patch.id !== undefined) out.id = patch.id;
  Object.assign(out.pos, patch.pos);
  Object.assign(out.size, patch.size);
  Object.assign(out.edge, patch.edge);
  // Annotations replace whole-array when the patch provides them (the canvas always sends the
  // complete current set, so add / move / delete all express as "here is the new array").
  if (patch.notes !== undefined) out.notes = patch.notes.map(cloneNote);
  if (patch.lanes !== undefined) out.lanes = patch.lanes.map(cloneLane);
  if (patch.canvas) out.canvas = { zoom: patch.canvas.zoom, center: [...patch.canvas.center] };
  return out;
}

/** Structural clone of a sticky note (plain data — no shared references). */
export function cloneNote(n: StickyNote): StickyNote {
  const out: StickyNote = { id: n.id, text: n.text, pos: [n.pos[0], n.pos[1]] };
  if (n.size) out.size = [n.size[0], n.size[1]];
  if (n.color) out.color = n.color;
  return out;
}

/** Structural clone of a swimlane (plain data — no shared references). */
export function cloneLane(l: Swimlane): Swimlane {
  const out: Swimlane = { id: l.id, label: l.label, pos: [l.pos[0], l.pos[1]], size: [l.size[0], l.size[1]] };
  if (l.color) out.color = l.color;
  if (l.orientation) out.orientation = l.orientation;
  return out;
}

/** Structural clone of a sidecar payload (plain data — no shared references). */
export function cloneSidecar(s: SidecarData): SidecarData {
  const out: SidecarData = {
    version: s.version,
    pos: {},
    size: {},
    edge: {},
  };
  if (s.id !== undefined) out.id = s.id;
  for (const [k, v] of Object.entries(s.pos)) out.pos[k] = [v[0], v[1]];
  for (const [k, v] of Object.entries(s.size)) out.size[k] = [v[0], v[1]];
  for (const [k, v] of Object.entries(s.edge)) out.edge[k] = { wp: v.wp.map((p) => [p[0], p[1]] as Point) };
  if (s.notes) out.notes = s.notes.map(cloneNote);
  if (s.lanes) out.lanes = s.lanes.map(cloneLane);
  if (s.canvas) out.canvas = { zoom: s.canvas.zoom, center: [...s.canvas.center] };
  return out;
}

/**
 * Render a `SidecarData` to a single canonical comment line (no trailing newline). Emits keys
 * in a stable order (`version, id, pos, size, edge, canvas`) and drops empty maps, so the
 * output is deterministic and diff-friendly. Preserves the caller's indentation prefix (a
 * sidecar inside a subgraph stays indented).
 *
 * Empty sidecars (no positions/sizes/edges/id/canvas) serialize to `''` — the caller treats
 * that as "no sidecar line at all", which keeps un-adjusted diagrams free of noise (§3 sparse).
 */
export function serializeSidecar(s: SidecarData, indent = ''): string {
  const payload: Record<string, unknown> = { version: s.version };
  if (s.id !== undefined) payload.id = s.id;
  if (Object.keys(s.pos).length > 0) payload.pos = s.pos;
  if (Object.keys(s.size).length > 0) payload.size = s.size;
  if (Object.keys(s.edge).length > 0) payload.edge = s.edge;
  if (s.notes && s.notes.length > 0) payload.notes = s.notes;
  if (s.lanes && s.lanes.length > 0) payload.lanes = s.lanes;
  if (s.canvas) payload.canvas = s.canvas;

  // Nothing beyond the version → treat as empty (no line).
  if (Object.keys(payload).length <= 1) return '';
  return `${indent}%% @aletheia:${s.version} ${JSON.stringify(payload)}`;
}

/** True if a sidecar has no geometry worth persisting (only a version, no id/pos/size/edge/canvas). */
export function isSidecarEmpty(s: SidecarData | null): boolean {
  if (!s) return true;
  return (
    s.id === undefined &&
    Object.keys(s.pos).length === 0 &&
    Object.keys(s.size).length === 0 &&
    Object.keys(s.edge).length === 0 &&
    !(s.notes && s.notes.length > 0) &&
    !(s.lanes && s.lanes.length > 0) &&
    !s.canvas
  );
}

/**
 * Reconcile a sidecar against the current graph (plan §3 "reconciliation on parse"). Drops:
 *   - position/size overrides for node ids that no longer exist;
 *   - edge waypoint overrides whose edge key no longer exists.
 * Keeps `id` and `canvas` untouched. Returns a new payload (inputs unmutated).
 *
 * This is the *prune* step — run when committing a canvas edit so a deleted node's stale
 * coordinates don't linger in the note. It is intentionally NOT run at read time (a
 * transiently-missing id during editing should survive for undo — plan §3).
 */
export function reconcileSidecar(
  s: SidecarData | null,
  liveNodeIds: ReadonlySet<string>,
  liveEdgeKeys: ReadonlySet<string>,
): SidecarData | null {
  if (!s) return null;
  const out = cloneSidecar(s);
  for (const id of Object.keys(out.pos)) if (!liveNodeIds.has(id)) delete out.pos[id];
  for (const id of Object.keys(out.size)) if (!liveNodeIds.has(id)) delete out.size[id];
  for (const key of Object.keys(out.edge)) if (!liveEdgeKeys.has(key)) delete out.edge[key];
  return out;
}

/**
 * Convenience: reconcile a sidecar against a parsed `DiagramModel`'s live ids/keys. Thin
 * wrapper over `reconcileSidecar` for the common "prune against this model" call.
 */
export function reconcileAgainstModel(s: SidecarData | null, model: DiagramModel): SidecarData | null {
  const nodeIds = new Set(model.nodes.keys());
  const edgeKeys = new Set(model.edges.map((e) => e.key));
  return reconcileSidecar(s, nodeIds, edgeKeys);
}

/** Re-export for callers that build a fresh payload. */
export { emptySidecar };
